import { Component, OnDestroy, OnInit, ViewEncapsulation } from '@angular/core';
import { ActivatedRoute, NavigationEnd, Router } from '@angular/router';
import { Subject, of } from 'rxjs';
import { catchError, filter, takeUntil } from 'rxjs/operators';
import { FuseMediaWatcherService } from '@fuse/services/media-watcher';
import { FuseNavigationService } from '@fuse/components/navigation';
import { FuseConfigService } from '@fuse/services/config';
import { ProfileService } from 'app/modules/profile/profile.service';
import { Profile } from 'app/modules/profile/profile.model';
import { InitialData } from 'app/app.types';
import {
    LayoutHeaderContextMenuItem,
    LayoutHeaderToolbarAction,
    LayoutHeaderToolbarService,
} from 'app/shared/layout/layout-header-toolbar.service';
import { resolveLayoutModuleTitle } from 'app/shared/layout/layout-module-title.util';
import { SessionNavigationService } from 'app/shared/navigation/session-navigation.service';
import { bindLayoutInitialData } from 'app/layout/layout-initial-data.binder';
import { isAshcorpBoHost } from 'app/modules/services-store/services-host.util';
import { BUILD_NO } from 'app/shared/build-info';
import { environment } from 'environments/environment';

@Component({
    selector: 'classy-layout',
    templateUrl: './classy.component.html',
    styleUrls: ['./classy.component.scss'],
    encapsulation: ViewEncapsulation.None,
    standalone: false
})
export class ClassyLayoutComponent implements OnInit, OnDestroy {
    readonly buildNo = BUILD_NO;
    readonly showBuildNo = !environment.production;
    data: InitialData;
    user: Profile;
    isScreenSmall: boolean;
    logoPath: string;
    watermarkPath = environment.layout?.watermark || 'assets/branding/ashcorp-watermark.png';
    headerToolbarActions: LayoutHeaderToolbarAction[] = [];
    headerContextMenuItems: LayoutHeaderContextMenuItem[] = [];
    navigationOpened = true;
    headerModuleTitle: string | null = null;
    private _unsubscribeAll: Subject<any> = new Subject<any>();

    constructor(
        private _activatedRoute: ActivatedRoute,
        private _router: Router,
        private _fuseMediaWatcherService: FuseMediaWatcherService,
        private _fuseNavigationService: FuseNavigationService,
        private _fuseConfigService: FuseConfigService,
        private _userService: ProfileService,
        private _sessionNavigation: SessionNavigationService,
        private _headerToolbar: LayoutHeaderToolbarService,
    ) {}

    get userDisplayName(): string {
        if (!this.user) {
            return '';
        }

        const profile = this.user as Profile & { fullname?: string };
        const fromParts = `${profile.othernames || ''} ${profile.lastname || ''}`.trim();
        return (profile.fullname || fromParts || profile.email || '').trim();
    }

    get showUserEmail(): boolean {
        if (!this.user?.email) {
            return false;
        }

        return this.userDisplayName.toLowerCase() !== this.user.email.trim().toLowerCase();
    }

    /**
     * Fuse demo header utilities hidden on BO until wired to real platform data.
     * @see co.ashcorp.docs/04-cc/FUTURE-DEVELOPMENT.md — Shell header utilities
     */
    get showHeaderUtilities(): boolean {
        return !isAshcorpBoHost();
    }

    ngOnInit(): void {
        bindLayoutInitialData(
            this._activatedRoute,
            this._sessionNavigation,
            this._unsubscribeAll,
            (data) => { this.data = data; }
        );

        this._userService.user$
            .pipe(takeUntil(this._unsubscribeAll))
            .subscribe((user: Profile) => {
                this.user = user;
            });

        // The auth/session payload does not carry the avatar, so the sidebar
        // would only show the placeholder until the Profile page is opened.
        // Fetch the full profile (including avatar) once on shell init.
        this._userService.load()
            .pipe(
                catchError(() => of(null)),
                takeUntil(this._unsubscribeAll),
            )
            .subscribe();

        this._fuseMediaWatcherService.onMediaChange$
            .pipe(takeUntil(this._unsubscribeAll))
            .subscribe(({ matchingAliases }) => {
                this.isScreenSmall = !matchingAliases.includes('md');
                this.navigationOpened = !this.isScreenSmall;
            });

        this._router.events
            .pipe(
                filter((event) => event instanceof NavigationEnd),
                takeUntil(this._unsubscribeAll),
            )
            .subscribe(() => this._syncHeaderModuleTitle());
        this._syncHeaderModuleTitle();

        this._fuseConfigService.config$
            .pipe(takeUntil(this._unsubscribeAll))
            .subscribe((config) => {
                this.logoPath = config.logo || this.logoPath;
                this.watermarkPath = config.watermark || this.watermarkPath;
            });

        this._headerToolbar.actions$
            .pipe(takeUntil(this._unsubscribeAll))
            .subscribe((actions) => {
                this.headerToolbarActions = actions;
            });

        this._headerToolbar.contextMenu$
            .pipe(takeUntil(this._unsubscribeAll))
            .subscribe((items) => {
                this.headerContextMenuItems = items;
            });
    }

    ngOnDestroy(): void {
        this._unsubscribeAll.next(null);
        this._unsubscribeAll.complete();
    }

    toggleNavigation(name: string): void {
        const navigation = this._fuseNavigationService.getComponent(name);

        if (navigation) {
            navigation.toggle();
        }
    }

    onNavigationOpenedChanged(opened: boolean): void {
        this.navigationOpened = opened;
    }

    trackToolbarAction(_index: number, action: LayoutHeaderToolbarAction): string {
        return action.id;
    }

    runToolbarAction(action: LayoutHeaderToolbarAction): void {
        action.handler();
    }

    trackContextMenuItem(_index: number, item: LayoutHeaderContextMenuItem): string {
        return item.id;
    }

    private _syncHeaderModuleTitle(): void {
        const root = this._router.routerState.snapshot.root;
        this.headerModuleTitle = resolveLayoutModuleTitle(root, this._router.url);
    }
}
